/**
 * MARKETING COMPASS — client API.
 *
 * The quiz UI NEVER touches Firestore directly: every marketing* collection is
 * client-write-denied in firestore.rules, and reads go through callables so the
 * server stays the single source of truth on entitlement and validation.
 * This module is the only place the marketing callables are named.
 */
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

export const getMarketingQuizResponseFn = httpsCallable(functions, 'getMarketingQuizResponse');
export const saveMarketingAnswerFn      = httpsCallable(functions, 'saveMarketingAnswer');
export const startMarketingQuizFn       = httpsCallable(functions, 'startMarketingQuiz');
export const completeMarketingQuizFn    = httpsCallable(functions, 'completeMarketingQuiz');
export const getMarketingRecommendationFn = httpsCallable(functions, 'getMarketingRecommendation');
export const getMarketingFeedbackPromptFn = httpsCallable(functions, 'getMarketingFeedbackPrompt');
export const submitMarketingFeedbackFn    = httpsCallable(functions, 'submitMarketingFeedback');

/** Outcome values the server accepts, with member-facing labels. */
export const FEEDBACK_OUTCOMES = [
  { value: 'helped', label: 'It helped' },
  { value: 'no_difference', label: 'No difference' },
  { value: 'wasted_money', label: 'Wasted money' },
  { value: 'too_hard', label: 'Too hard to use' },
];

/**
 * Load the caller's in-progress quiz, if any.
 * @returns {Promise<{found: boolean, responseId?: string, answers?: object, prefilled?: object}>}
 */
export async function loadQuizResponse() {
  const res = await getMarketingQuizResponseFn();
  return res?.data || { found: false, inProgress: false, completed: false };
}

/**
 * Begin a quiz.
 *   'fresh' — a brand-new blank response. Previous ones stay completed and
 *             their recommendations stay in history; nothing is deleted.
 *   'edit'  — reopen the finished response in place so its answers load.
 * Already mid-quiz? Both are a no-op that resumes what's there.
 */
export async function startQuiz(mode = 'fresh') {
  const res = await startMarketingQuizFn({ mode });
  return res?.data || { ok: false };
}

/**
 * Persist a single answer. Called on every question so an abandoned quiz
 * resumes exactly where it was left.
 *
 * @param {string} questionId
 * @param {string|string[]} answer
 * @param {string|null} prefilledSource  the platform field this came from, or
 *   null when the member chose it themselves (which clears any stale marker).
 */
export async function saveAnswer(questionId, answer, prefilledSource = null, freeText = null) {
  const res = await saveMarketingAnswerFn({
    questionId,
    answer,
    ...(prefilledSource ? { prefilled: prefilledSource } : {}),
    // Only meaningful for a question whose selected option declares freeText;
    // the server drops it otherwise.
    ...(freeText ? { freeText } : {}),
  });
  return res?.data || { ok: false };
}

/** Mark the quiz complete. Rejects with failed-precondition if any are unanswered. */
export async function completeQuiz() {
  const res = await completeMarketingQuizFn();
  return res?.data || { ok: false };
}

/**
 * Fetch the recommendation for the caller's completed quiz.
 *
 * The engine is deterministic, so the server returns the stored result unless
 * `refresh` is set — re-running produces the same output for the same answers,
 * it just writes a fresh doc and retires the previous one.
 */
export async function getRecommendation(refresh = false) {
  const res = await getMarketingRecommendationFn({ refresh });
  return res?.data || null;
}

/**
 * Is a 30-day feedback prompt due for this member?
 *
 * Returns { due: false } for every reason it might not be — no recommendation,
 * too soon, snoozed, dismissed, already answered — so the caller never has to
 * reason about the timing itself.
 */
export async function getFeedbackPrompt() {
  try {
    const res = await getMarketingFeedbackPromptFn();
    return res?.data || { due: false };
  } catch (err) {
    // A prompt is a nicety. It must never break the hub, so a failure here is
    // swallowed rather than surfaced.
    console.warn('[marketing] feedback prompt check failed:', err?.code, err?.message);
    return { due: false };
  }
}

/** Submit feedback entries for a recommendation. */
export async function submitFeedback(recommendationId, entries) {
  const res = await submitMarketingFeedbackFn({ recommendationId, action: 'submit', entries });
  return res?.data || { ok: false };
}

/** Snooze the prompt for a week. */
export async function snoozeFeedback(recommendationId) {
  const res = await submitMarketingFeedbackFn({ recommendationId, action: 'snooze' });
  return res?.data || { ok: false };
}

/** Dismiss the prompt for good — it never returns for this recommendation. */
export async function dismissFeedback(recommendationId) {
  const res = await submitMarketingFeedbackFn({ recommendationId, action: 'dismiss' });
  return res?.data || { ok: false };
}

/** Map a callable error to something worth showing a member. */
export function marketingErrorMessage(err) {
  const code = err?.code || '';
  if (code.includes('unauthenticated')) return 'Please sign in to use Marketing Compass.';
  if (code.includes('permission-denied')) {
    return err?.message || 'Marketing Compass is included with a Roadmap or Bundle licence.';
  }
  if (code.includes('invalid-argument')) return "That answer wasn't recognised — please pick one of the options.";
  if (code.includes('failed-precondition')) return 'Finish the quiz first and we\'ll build your stack.';
  return "Something went wrong saving your answer. Your earlier answers are safe — please try again.";
}
